import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';

const ContentModeration = () => {
  const [flaggedContent, setFlaggedContent] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    loadFlaggedContent();
  }, []);

  const loadFlaggedContent = async () => {
    setLoading(true);
    try {
      // Load flagged reviews
      const { data: reviews, error: reviewsError } = await supabase?.from('reviews')?.select(`
          id,
          rating,
          comment,
          created_at,
          is_flagged,
          flag_reason,
          reviewer:user_profiles!reviews_reviewer_id_fkey(id, full_name, email),
          reviewed:user_profiles!reviews_reviewed_id_fkey(id, full_name, email)
        `)?.eq('is_flagged', true)?.order('created_at', { ascending: false });

      // Load flagged/inactive listings
      const { data: listings, error: listingsError } = await supabase?.from('trailer_listings')?.select(`
          id,
          title,
          description,
          status,
          created_at,
          photos,
          owner:user_profiles!trailer_listings_owner_id_fkey(id, full_name, email)
        `)?.in('status', ['flagged', 'suspended'])?.order('created_at', { ascending: false });

      const combined = [];

      if (reviews && !reviewsError) {
        reviews?.forEach(r => combined?.push({
          id: `review-${r?.id}`,
          rawId: r?.id,
          type: 'review',
          title: `Review by ${r?.reviewer?.full_name || r?.reviewer?.email || 'Unknown'}`,
          content: r?.comment || 'No comment',
          author: r?.reviewer?.full_name || r?.reviewer?.email || 'Unknown',
          target: r?.reviewed?.full_name || r?.reviewed?.email || 'Unknown',
          reason: r?.flag_reason || 'Flagged for review',
          status: 'pending',
          severity: r?.rating <= 1 ? 'high' : 'medium',
          createdDate: r?.created_at,
          flagCount: 1
        }));
      }

      if (listings && !listingsError) {
        listings?.forEach(l => combined?.push({
          id: `listing-${l?.id}`,
          rawId: l?.id,
          type: 'listing',
          title: l?.title || 'Untitled Listing',
          content: l?.description || 'No description',
          author: l?.owner?.full_name || l?.owner?.email || 'Unknown',
          image: l?.photos?.[0] || null,
          reason: `Listing status: ${l?.status}`,
          status: l?.status === 'flagged' ? 'pending' : 'under_review',
          severity: 'medium',
          createdDate: l?.created_at,
          flagCount: 1
        }));
      }

      setFlaggedContent(combined);
    } catch (error) {
      console.error('Error loading flagged content:', error);
      setFlaggedContent([]);
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleApprove = async (item) => {
    setActionLoading(true);
    try {
      if (item?.type === 'review') {
        await supabase?.from('reviews')?.update({ is_flagged: false })?.eq('id', item?.rawId);
      } else if (item?.type === 'listing') {
        await supabase?.from('trailer_listings')?.update({ status: 'active' })?.eq('id', item?.rawId);
      }
      setFlaggedContent(prev => prev?.filter(c => c?.id !== item?.id));
      showToast('Content approved and restored');
    } catch (error) {
      showToast('Failed to approve content', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemove = async (item) => {
    setActionLoading(true);
    try {
      if (item?.type === 'review') {
        await supabase?.from('reviews')?.update({ is_flagged: true, flag_reason: 'Removed by admin' })?.eq('id', item?.rawId);
      } else if (item?.type === 'listing') {
        await supabase?.from('trailer_listings')?.update({ status: 'deleted' })?.eq('id', item?.rawId);
      }
      setFlaggedContent(prev => prev?.map(c => c?.id === item?.id ? { ...c, status: 'removed' } : c));
      showToast('Content removed successfully');
    } catch (error) {
      showToast('Failed to remove content', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'pending': return { label: 'Pending Review', color: 'bg-warning/10 text-warning', icon: 'Clock' };
      case 'under_review': return { label: 'Under Review', color: 'bg-primary/10 text-primary', icon: 'Eye' };
      case 'approved': return { label: 'Approved', color: 'bg-success/10 text-success', icon: 'CheckCircle' };
      case 'removed': return { label: 'Removed', color: 'bg-error/10 text-error', icon: 'Trash2' };
      default: return { label: 'Unknown', color: 'bg-muted text-muted-foreground', icon: 'HelpCircle' };
    }
  };

  const getSeverityConfig = (severity) => {
    switch (severity) {
      case 'high': return { label: 'High', color: 'text-error' };
      case 'medium': return { label: 'Medium', color: 'text-warning' };
      case 'low': return { label: 'Low', color: 'text-success' };
      default: return { label: 'Normal', color: 'text-muted-foreground' };
    }
  };

  const filteredContent = flaggedContent?.filter((c) => {
    if (filter === 'all') return true;
    return c?.status === filter;
  });

  const pendingCount = flaggedContent?.filter((c) => c?.status === 'pending')?.length;
  const underReviewCount = flaggedContent?.filter((c) => c?.status === 'under_review')?.length;
  const removedCount = flaggedContent?.filter((c) => c?.status === 'removed')?.length;

  return (
    <div className="space-y-6">
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg font-body text-sm font-medium ${
          toast?.type === 'error' ? 'bg-error text-white' : 'bg-success text-white'
        }`}>
          {toast?.message}
        </div>
      )}
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-card rounded-lg shadow-elevation-2 p-5">
          <div className="w-10 h-10 bg-warning/10 rounded-full flex items-center justify-center mb-3">
            <Icon name="Flag" size={20} strokeWidth={2} className="text-warning" />
          </div>
          <p className="font-heading text-2xl font-bold text-foreground mb-1">{pendingCount}</p>
          <p className="font-caption text-sm text-muted-foreground">Pending Review</p>
        </div>
        <div className="bg-card rounded-lg shadow-elevation-2 p-5">
          <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center mb-3">
            <Icon name="Eye" size={20} strokeWidth={2} className="text-primary" />
          </div>
          <p className="font-heading text-2xl font-bold text-foreground mb-1">{underReviewCount}</p>
          <p className="font-caption text-sm text-muted-foreground">Under Review</p>
        </div>
        <div className="bg-card rounded-lg shadow-elevation-2 p-5">
          <div className="w-10 h-10 bg-error/10 rounded-full flex items-center justify-center mb-3">
            <Icon name="Trash2" size={20} strokeWidth={2} className="text-error" />
          </div>
          <p className="font-heading text-2xl font-bold text-foreground mb-1">{removedCount}</p>
          <p className="font-caption text-sm text-muted-foreground">Removed</p>
        </div>
        <div className="bg-card rounded-lg shadow-elevation-2 p-5">
          <div className="w-10 h-10 bg-success/10 rounded-full flex items-center justify-center mb-3">
            <Icon name="Shield" size={20} strokeWidth={2} className="text-success" />
          </div>
          <p className="font-heading text-2xl font-bold text-foreground mb-1">{flaggedContent?.length}</p>
          <p className="font-caption text-sm text-muted-foreground">Total Flagged</p>
        </div>
      </div>
      {/* Filter Tabs */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-2 flex items-center justify-between">
        <div className="flex space-x-2 flex-wrap gap-1">
          {['all', 'pending', 'under_review', 'approved', 'removed']?.map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-2 rounded-md font-body text-sm font-medium transition-smooth capitalize ${
                filter === status ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'
              }`}
            >
              {status?.replace('_', ' ')}
            </button>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={loadFlaggedContent}>
          <Icon name="RefreshCw" size={14} strokeWidth={2} />
        </Button>
      </div>
      {/* Flagged Content List */}
      <div className="bg-card rounded-lg shadow-elevation-2">
        <div className="p-6 border-b border-border">
          <h3 className="font-heading text-lg font-semibold text-foreground flex items-center space-x-2">
            <Icon name="Flag" size={20} strokeWidth={2} className="text-primary" />
            <span>Flagged Content ({filteredContent?.length})</span>
          </h3>
        </div>
        <div className="divide-y divide-border">
          {loading ? (
            <div className="p-8 text-center">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3" />
              <p className="font-body text-muted-foreground">Loading flagged content...</p>
            </div>
          ) : filteredContent?.length === 0 ? (
            <div className="p-8 text-center">
              <Icon name="Inbox" size={48} strokeWidth={1.5} className="text-muted-foreground mx-auto mb-3" />
              <p className="font-body text-muted-foreground">No flagged content found</p>
              <p className="font-body text-xs text-muted-foreground mt-1">Platform content is clean</p>
            </div>
          ) : (
            filteredContent?.map((item) => {
              const statusConfig = getStatusConfig(item?.status);
              const severityConfig = getSeverityConfig(item?.severity);
              return (
                <div key={item?.id} className="p-5 hover:bg-muted/50 transition-smooth">
                  <div className="flex items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center flex-wrap gap-2 mb-2">
                        <h4 className="font-body font-semibold text-foreground">{item?.title}</h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusConfig?.color} flex items-center space-x-1`}>
                          <Icon name={statusConfig?.icon} size={12} strokeWidth={2} />
                          <span>{statusConfig?.label}</span>
                        </span>
                        <span className={`text-xs font-medium ${severityConfig?.color}`}>
                          {severityConfig?.label} Severity
                        </span>
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground capitalize">
                          {item?.type}
                        </span>
                      </div>
                      <div className="flex items-center flex-wrap gap-3 text-xs text-muted-foreground mb-2">
                        <span className="flex items-center space-x-1">
                          <Icon name="User" size={12} strokeWidth={2} />
                          <span>{item?.author}</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <Icon name="Flag" size={12} strokeWidth={2} />
                          <span>{item?.flagCount} report(s)</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <Icon name="Calendar" size={12} strokeWidth={2} />
                          <span>{new Date(item?.createdDate)?.toLocaleDateString()}</span>
                        </span>
                      </div>
                      <div className="bg-muted rounded-lg p-3 mb-3">
                        <p className="font-body text-xs font-medium text-muted-foreground mb-1">Reason: {item?.reason}</p>
                        <p className="font-body text-sm text-foreground line-clamp-2">{item?.content}</p>
                      </div>
                      {(item?.status === 'pending' || item?.status === 'under_review') && (
                        <div className="flex items-center gap-2">
                          <Button
                            variant="success"
                            size="sm"
                            disabled={actionLoading}
                            onClick={() => handleApprove(item)}
                          >
                            <Icon name="CheckCircle" size={14} strokeWidth={2} />
                            <span className="ml-1">Approve</span>
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            disabled={actionLoading}
                            onClick={() => handleRemove(item)}
                          >
                            <Icon name="Trash2" size={14} strokeWidth={2} />
                            <span className="ml-1">Remove</span>
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
      {/* Automated Filters Info */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-6">
        <h3 className="font-heading text-lg font-semibold text-foreground mb-4 flex items-center space-x-2">
          <Icon name="Shield" size={20} strokeWidth={2} className="text-primary" />
          <span>Automated Content Filtering</span>
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="Star" size={16} strokeWidth={2} className="text-primary" />
              <span className="font-body text-sm font-medium">Review Monitoring</span>
            </div>
            <p className="font-body text-xs text-muted-foreground">Flagged reviews appear here for admin review</p>
          </div>
          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="Package" size={16} strokeWidth={2} className="text-primary" />
              <span className="font-body text-sm font-medium">Listing Oversight</span>
            </div>
            <p className="font-body text-xs text-muted-foreground">Suspended or flagged listings for admin action</p>
          </div>
          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="AlertTriangle" size={16} strokeWidth={2} className="text-primary" />
              <span className="font-body text-sm font-medium">Violation Tracking</span>
            </div>
            <p className="font-body text-xs text-muted-foreground">Tracks user violations and policy breaches</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentModeration;